import { QuestType } from "src/hooks/affiliate";

/**
 * คำอธิบาย debug ต่อ quest type — ถอดเนื้อหาจาก
 * pigslot-service/affiliate/src/docs/pages/quest_debug.md
 * ใช้แสดงในปุ่ม "เช็คสถานะ" ของหน้า Progress Quest เพื่ออธิบายว่าแต่ละ quest
 * ทำงานยังไง + เหตุผลที่ progress อาจไม่ขยับ
 */
export interface QuestDebugInfo {
  /** quest นี้ทำงานยังไง (สั้น ๆ) */
  howItWorks: string;
  /** event ที่ trigger */
  trigger: string;
  /** ตาราง/ค่าที่เป็น source of truth */
  sourceOfTruth: string;
  /** config ที่เกี่ยวข้อง */
  config: string[];
  /** เหตุผลที่ progress ไม่ขยับ / quest ไม่ trigger */
  notTrigger: string[];
}

export const QUEST_DEBUG_INFO: Record<QuestType, QuestDebugInfo> = {
  [QuestType.REFERRAL_COUNT]: {
    howItWorks:
      "นับ downline ที่ฝากเงินครั้งแรก (first deposit) — fire ครั้งเดียวต่อ downline เมื่อ deposit_count == 1 พอดี",
    trigger: "deposit event",
    sourceOfTruth: "affiliate_user_aggregate.deposit_count + affiliate_quest_user_map (dedup)",
    config: ["condition.time_window_day (optional) — นับเฉพาะ first-deposit ภายใน N วันย้อนหลัง"],
    notTrigger: [
      "deposit_count != 1 (ยังไม่ฝาก หรือฝากเกินครั้งแรกไปแล้ว)",
      "downline ถูกนับไปแล้ว (already counted)",
      "ตั้ง time_window_day และ first_deposit เกินช่วงเวลา",
    ],
  },
  [QuestType.REFERRAL_STACK]: {
    howItWorks:
      "นับ downline ที่ฝากครบ N ครั้งขึ้นไป — fire เมื่อ deposit_count == min_per_user (ครั้งแรกที่ถึง)",
    trigger: "deposit event",
    sourceOfTruth: "affiliate_user_aggregate.deposit_count + affiliate_quest_user_map (dedup)",
    config: ["extra_config.min_per_user (default 2)"],
    notTrigger: [
      "deposit_count ยังไม่ถึง min_per_user",
      "deposit_count เกิน min_per_user (fire ไปก่อนหน้าแล้ว)",
      "downline ถูกนับไปแล้ว (already counted)",
    ],
  },
  [QuestType.REFERRAL_TURNOVER]: {
    howItWorks:
      "ยอด turnover รวมระดับ upline (สะสมจากทุก downline ในเครือ) ถึงเป้า — เพิ่ม progress ทุก bet, COMPLETED เมื่อ current_value >= target_value (sticky)",
    trigger: "bet event",
    sourceOfTruth: "affiliate_campaign_user_quest_progress.current_value (ยอดบาทสะสม)",
    config: ["target_value = ยอด turnover รวม (บาท) — ไม่มี extra_config"],
    notTrigger: [
      "ยังมี bet ไม่พอให้ยอดสะสมถึง target_value",
      "bet เกิดก่อน quest start จะไม่ถูกนับ (ไม่ผ่าน branch)",
    ],
  },
  [QuestType.REFERRAL_TURNOVER_CONTINUE]: {
    howItWorks:
      "นับ downline ที่เล่นติดต่อกัน N วัน — fire เมื่อ current_streak == min_streak พอดี",
    trigger: "bet event",
    sourceOfTruth:
      "affiliate_user_aggregate.current_streak + affiliate_quest_user_map (ถ้า is_unique_user)",
    config: ["extra_config.min_streak (default 2)", "condition.is_unique_user"],
    notTrigger: [
      "current_streak != min_streak (ยังไม่ถึง หรือเลยไปแล้ว)",
      "ห่างเกิน 1 วัน → streak reset เป็น 1 ต้องเริ่มต่อเนื่องใหม่",
      "is_unique_user = true และ downline ถูกนับไปแล้ว",
    ],
  },
  [QuestType.REFERRAL_INVITE_PLAYERS]: {
    howItWorks:
      "นับ downline ที่เล่นในค่าย (game_provider) หรือ product_type ที่กำหนด — fire ครั้งเดียวต่อ downline",
    trigger: "bet event",
    sourceOfTruth: "affiliate_user_provider_aggregate + affiliate_quest_user_map (dedup)",
    config: [
      "extra_config.game_provider (optional)",
      "extra_config.product_type (optional) — อย่างน้อย 1 ใน 2 ต้องตั้ง",
      "extra_config.min_bet_amount (default 0)",
    ],
    notTrigger: [
      "bet ไม่ได้อยู่ในค่าย/product เป้าหมาย",
      "ยอดเดิมพัน < min_bet_amount",
      "ไม่ได้ตั้ง provider และ product_type เลย → branch skip",
      "downline ถูกนับไปแล้ว",
    ],
  },
  [QuestType.REFERRAL_INVITE_PROVIDER_ACTIVE_DURATION]: {
    howItWorks:
      "นับ downline ที่เล่นใน scope ครบ N วันต่างกัน (distinct days, BKK) — fire เมื่อ distinct count แตะ min_active_days",
    trigger: "bet event",
    sourceOfTruth: "affiliate_quest_active_day_map + affiliate_quest_user_map (dedup)",
    config: [
      "extra_config.game_provider / product_type / game_id — อย่างน้อย 1 ใน 3 ต้องตั้ง",
      "extra_config.min_active_days (default 1)",
      "extra_config.min_bet_amount (default 0)",
    ],
    notTrigger: [
      "จำนวนวัน active ยังไม่ถึง min_active_days",
      "active days เกิน min_active_days แล้ว (เคย fire — ดู already counted)",
      "bet ไม่ผ่าน filter (provider/product/game_id หรือ < min_bet)",
      "ไม่ตั้ง provider/product/game_id เลย → skip",
    ],
  },
  [QuestType.AFFILIATE_INVITE_CONSECUTIVE_PLAYERS]: {
    howItWorks:
      "Streak ระดับ upline — วันใดมี downline (ครบ min_downlines_per_day) วาง bet = +1, วันใดไม่ครบ = reset เป็น 0",
    trigger: "bet event (+ decay batch เที่ยงคืน BKK)",
    sourceOfTruth:
      "affiliate_quest_consecutive_progress.last_active_date + affiliate_campaign_user_quest_progress.current_value",
    config: [
      "extra_config.min_bet_amount (default 0)",
      "extra_config.min_downlines_per_day (default 1)",
      "ต้องมี decay batch รัน midnight BKK ทุกวัน",
    ],
    notTrigger: [
      "วันนี้ยังไม่มี downline วาง bet → progress ค้างค่าเดิม (stale) จนกว่า batch จะรัน",
      "gap >= 2 วันตอน bet ครั้งใหม่ → reset เป็น 1",
      "decay batch ไม่ได้ตั้งเป็น cron → progress stale ค้างไว้",
    ],
  },
  [QuestType.REFERRAL_TURNOVER_GAME_PROVIDERS]: {
    howItWorks:
      "นับ downline ที่ turnover สะสมในค่าย (หรือเกมเฉพาะ) ถึง X บาท — fire ครั้งเดียวต่อ downline เมื่อข้าม min_turnover",
    trigger: "bet event",
    sourceOfTruth:
      "affiliate_user_provider_aggregate / affiliate_user_game_aggregate (ถ้ามี game_id) + affiliate_quest_user_map",
    config: [
      "extra_config.game_provider / product_type / game_id — อย่างน้อย 1 ใน 3 ต้องตั้ง",
      "extra_config.min_turnover (required, > 0)",
    ],
    notTrigger: [
      "turnover ใน scope < min_turnover",
      "turnover ถึง threshold ก่อน quest เริ่ม (branch ไม่เห็น transition)",
      "downline ถูกนับไปแล้ว",
      "bet ไม่ผ่าน filter (provider/product/game_id)",
    ],
  },
  [QuestType.REFERRAL_INVITE_DISTINCT_GAME_PROVIDERS]: {
    howItWorks:
      "นับ downline ที่เล่นเกมต่างกัน (game_id ต่างกัน) ภายใน scope ครบ N เกม",
    trigger: "bet event (ต้องมี game_id)",
    sourceOfTruth: "affiliate_quest_distinct_game_map + affiliate_quest_user_map (dedup)",
    config: [
      "extra_config.game_provider / product_type — อย่างน้อย 1 ใน 2 ต้องตั้ง",
      "extra_config.min_distinct_games (default 1)",
      "extra_config.min_bet_amount (default 0)",
    ],
    notTrigger: [
      "จำนวนเกมต่างกันยังไม่ถึง min_distinct_games",
      "bet < min_bet_amount (ไม่ qualify)",
      "bet ไม่ผ่าน filter (provider/product mismatch)",
      "downline ถูกนับไปแล้ว",
    ],
  },
  [QuestType.REFERRAL_INVITE_COMEBACK_PLAYERS]: {
    howItWorks:
      "นับ downline ที่ดึงกลับมาเล่นหลังหายไปครบจำนวนวัน — fire เมื่อ gap จาก last_active_date ≥ inactive_days",
    trigger: "bet event",
    sourceOfTruth:
      "affiliate_user_aggregate.last_active_date (snapshot ก่อน update) + affiliate_quest_user_map",
    config: [
      "extra_config.inactive_days (default 30)",
      "extra_config.min_bet_amount (default 0)",
      "extra_config.game_provider / product_type (optional)",
    ],
    notTrigger: [
      "last_active_date เป็น NULL — downline ไม่เคย bet มาก่อน (ไม่ใช่ comeback)",
      "gap_days < inactive_days (ยังหายไปไม่ครบ)",
      "bet ไม่ผ่าน filter (provider/product/min_bet)",
      "downline ถูกนับไปแล้ว (comeback ครั้งเดียวต่อ quest)",
    ],
  },
};

export const getQuestDebugInfo = (type?: QuestType | string | null): QuestDebugInfo | undefined =>
  type ? QUEST_DEBUG_INFO[type as QuestType] : undefined;
